import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, Instagram } from "lucide-react";
import { toast } from "sonner";
import Reveal from "../components/Reveal";
import { useSite } from "../context/SiteContext";
import { firebaseReady, getFb } from "../lib/firebase";
import { addDoc, collection } from "firebase/firestore";

const COURSES_OPTIONS = [
    "Buraxılış imtahanlarına hazırlıq",
    "İngilis dili",
    "Riyaziyyat və fizika",
    "İbtidai sinif hazırlığı",
    "SAT / IELTS / TOEFL",
    "Olimpiada hazırlığı",
    "Yay proqramı",
];

const TikTokIcon = ({ size = 16 }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
    >
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.85a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31Z" />
    </svg>
);

export const Contact = ({ standalone = false }) => {
    const { content } = useSite();
    const c = content.contact || {};
    const social = content.social || {};
    const [form, setForm] = useState({
        parentName: "",
        studentName: "",
        phone: "",
        course: COURSES_OPTIONS[0],
        grade: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.parentName || !form.phone) {
            toast.error("Valideyn adı və telefon nömrəsi mütləqdir");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form, createdAt: new Date().toISOString() };
            if (firebaseReady()) {
                const fb = getFb();
                if (fb) {
                    await addDoc(collection(fb.db, "ka_messages"), payload);
                }
            } else {
                const list = JSON.parse(
                    localStorage.getItem("ka_messages") || "[]"
                );
                list.push(payload);
                localStorage.setItem("ka_messages", JSON.stringify(list));
            }
            toast.success(
                "Müraciətiniz qəbul edildi. Tezliklə sizinlə əlaqə saxlayacağıq."
            );
            setForm({
                parentName: "",
                studentName: "",
                phone: "",
                course: COURSES_OPTIONS[0],
                grade: "",
                message: "",
            });
        } catch (err) {
            console.error(err);
            toast.error("Göndərilərkən xəta baş verdi");
        } finally {
            setSubmitting(false);
        }
    };

    const waNum = (social.whatsapp || "").replace(/\D/g, "");

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className={`relative bg-[#FAFAFA] ${standalone ? "pt-36 pb-28" : "py-24 lg:py-32"}`}
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
                    <div className="lg:col-span-5 space-y-9">
                        <Reveal>
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#14545E]/10 text-[#14545E] text-[11px] tracking-[0.2em] uppercase mb-5">
                                {c.eyebrow}
                            </div>
                            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#0A1F3D] leading-[1.05]">
                                {c.title}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="text-neutral-600 leading-relaxed text-base lg:text-lg">
                                {c.body}
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <ul className="space-y-4">
                                <ContactRow
                                    icon={<Phone size={18} />}
                                    label="Telefon"
                                    value={
                                        <a
                                            href={`tel:${c.phone}`}
                                            data-testid="contact-phone"
                                            className="tabular text-xl font-serif-display text-[#0A1F3D]"
                                        >
                                            {c.phone}
                                        </a>
                                    }
                                />
                                <ContactRow
                                    icon={<Mail size={18} />}
                                    label="E-poçt"
                                    value={
                                        <a
                                            href={`mailto:${c.email}`}
                                            className="text-[#0A1F3D]"
                                        >
                                            {c.email}
                                        </a>
                                    }
                                />
                                <ContactRow
                                    icon={<MapPin size={18} />}
                                    label="Ünvan"
                                    value={c.address}
                                />
                                <ContactRow
                                    icon={<Clock size={18} />}
                                    label="İş saatları"
                                    value={c.workingHours}
                                />
                            </ul>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <div className="flex flex-wrap gap-3 pt-3 border-t border-neutral-200">
                                {waNum && (
                                    <a
                                        href={`https://wa.me/${waNum}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="contact-whatsapp-cta"
                                        className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white px-5 py-3 rounded-full text-sm transition-colors"
                                    >
                                        <MessageCircle size={16} />
                                        WhatsApp ilə yaz
                                    </a>
                                )}
                                {social.instagram && (
                                    <a
                                        href={social.instagram}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 border border-[#0A1F3D]/15 hover:border-[#0A1F3D] text-[#0A1F3D] px-5 py-3 rounded-full text-sm transition-colors"
                                    >
                                        <Instagram size={16} />
                                        Instagram
                                    </a>
                                )}
                                {social.tiktok && (
                                    <a
                                        href={social.tiktok}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 border border-[#0A1F3D]/15 hover:border-[#0A1F3D] text-[#0A1F3D] px-5 py-3 rounded-full text-sm transition-colors"
                                    >
                                        <TikTokIcon size={16} />
                                        TikTok
                                    </a>
                                )}
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.15} className="lg:col-span-7">
                        <form
                            onSubmit={submit}
                            data-testid="register-form"
                            className="bg-white border border-neutral-200 rounded-3xl p-7 lg:p-10 space-y-5 shadow-sm"
                        >
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#14545E]/10 text-[#14545E] text-[11px] tracking-[0.2em] uppercase">
                                Onlayn qeydiyyat
                            </div>
                            <h3 className="font-serif-display text-3xl lg:text-4xl text-[#0A1F3D]">
                                Müraciət forması
                            </h3>
                            <p className="text-sm text-neutral-600">
                                Məlumatlarınızı doldurun — koordinatorumuz 24 saat ərzində sizinlə əlaqə saxlayacaq.
                            </p>

                            <div className="grid sm:grid-cols-2 gap-4 pt-2">
                                <Input
                                    label="Valideyn adı *"
                                    value={form.parentName}
                                    onChange={(v) =>
                                        setForm({ ...form, parentName: v })
                                    }
                                    placeholder="Adınız Soyadınız"
                                    testId="form-parent-name"
                                />
                                <Input
                                    label="Şagird adı"
                                    value={form.studentName}
                                    onChange={(v) =>
                                        setForm({ ...form, studentName: v })
                                    }
                                    placeholder="Övladınızın adı"
                                    testId="form-student-name"
                                />
                                <Input
                                    label="Telefon *"
                                    value={form.phone}
                                    onChange={(v) => setForm({ ...form, phone: v })}
                                    placeholder="70 xxx xx xx"
                                    type="tel"
                                    testId="form-phone"
                                />
                                <Input
                                    label="Sinif"
                                    value={form.grade}
                                    onChange={(v) => setForm({ ...form, grade: v })}
                                    placeholder="Məs: 9-cu sinif"
                                    testId="form-grade"
                                />
                                <div className="sm:col-span-2">
                                    <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                                        Kurs
                                    </label>
                                    <select
                                        value={form.course}
                                        onChange={(e) =>
                                            setForm({ ...form, course: e.target.value })
                                        }
                                        data-testid="form-course"
                                        className="w-full bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-3 text-[#0A1F3D] outline-none transition-colors ka-input"
                                    >
                                        {COURSES_OPTIONS.map((c) => (
                                            <option key={c} value={c}>
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                                        Mesaj (ixtiyari)
                                    </label>
                                    <textarea
                                        rows={4}
                                        value={form.message}
                                        onChange={(e) =>
                                            setForm({ ...form, message: e.target.value })
                                        }
                                        data-testid="form-message"
                                        className="w-full bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-3 text-[#0A1F3D] placeholder:text-neutral-400 outline-none transition-colors resize-none ka-input"
                                        placeholder="Əlavə qeydləriniz"
                                    />
                                </div>
                            </div>

                            <p className="text-xs text-neutral-500 pt-1">
                                Məlumatlarınız konfidensialdır və yalnız əlaqə üçün istifadə olunur.
                            </p>

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="form-submit"
                                className="group inline-flex items-center gap-2 bg-[#0A1F3D] hover:bg-[#061632] disabled:opacity-50 text-white px-7 py-3.5 rounded-full text-sm transition-colors"
                            >
                                {submitting ? "Göndərilir..." : "Müraciəti göndər"}
                                <Send
                                    size={16}
                                    className="transition-transform group-hover:translate-x-1"
                                />
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

const ContactRow = ({ icon, label, value }) => (
    <li className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-xl bg-[#14545E]/10 text-[#14545E] flex items-center justify-center shrink-0">
            {icon}
        </div>
        <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-1">
                {label}
            </div>
            <div>{value}</div>
        </div>
    </li>
);

const Input = ({ label, value, onChange, placeholder, type = "text", testId }) => (
    <div>
        <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
            {label}
        </label>
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="w-full bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-3 text-[#0A1F3D] placeholder:text-neutral-400 outline-none transition-colors ka-input"
            placeholder={placeholder}
        />
    </div>
);

export default Contact;
